import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore,
} from 'react';
import {
  Table, Box, Select, TextInput,
} from '@mantine/core';
import * as TablerIcons from '@tabler/icons-react';
import styles from './EditableDataTable.module.css';
import i18n from '../../../../i18n';
import { isSplittableTable, tableTitleWithOptions } from '../../../../shared/tables/tableDescriptors';
import {
  getTableCatalogSnapshot, isTemplateTableCatalog, loadTableCatalog, subscribeTableCatalog,
} from '../../../utils/tableCatalogRegistry';
import {
  computeLabelRowMerges,
  computeMergedCellLayout,
  computeRepeatedValueMerges,
  isMultiSelectClick,
  mergedCellSpanKey,
} from '../../../../shared/tables/tableStructure';
import { cellStyleEntry, computeRowStyles, rowStyleCss } from '../../../../shared/tables/tableRowStyle';
import { fromPuckTableConfig } from '../../../utils/puckTableConfig';
import { getTablePreviewLayout, getTableRowStructure } from '../../../../shared/tables/tableDescriptors';
import {
  getTableSelection,
  setTableCellSelection,
  setTableColumnSelection,
  subscribeTableSelection,
} from '../../../utils/tableSelectionRegistry';
import { renderDraftContent } from '../../../utils/renderDraftContent';
import {
  ImageFrame,
  VideoFrame,
  getMediaSrc,
  isMediaPresent,
  MEDIA_KINDS,
  normalizeMediaSource,
} from '../../../media';
import { withVariablePlaceholder } from '../../../media/variableMedia';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { draftRawToHtml, isDraftRawContent } from '../../../utils/draftRawToHtml';
import { puckArrayFieldPath, puckNestedArrayFieldPath } from '../../../utils/puckContentPath';
import { subscribeColumnPreview } from '../../../utils/tableColumnsRegistry';
import { storedTableDesign, withTableStyleDefaults } from './tableStyles';
import {
  columnFilterValues, groupTitles, isSortableColumn, repeatsGroupStructure, rowRole, visibleRowIndexes,
} from './tableInteractions';
import { ariaSort, nextSort } from '../../../utils/tableSort';
import SortIcon from '../../../components/SortIcon';
import { useProposalProjectConfig } from '../../../contexts/ProposalProjectConfigContext';
import { themedTextFontFamily } from '../../../../shared/theme/themeTypography';
import { DEFAULT_SITE_FONT, LEGACY_DEFAULT_FONT } from '../../../../shared/theme/themeConfiguration';
import {
  columnOriginLabels,
  normalizeTableColumns,
  normalizeTableRows,
  plainCellText,
  resolveBorderScope,
  resolveCardHeader,
  resolveCellType,
  resolveColumnPixelWidths,
  resolveTableLayout,
  resolveTableSpacing,
  resolveTextAlign,
  resolveVerticalAlign,
  toCompareCards,
  toRowEntries,
  typedRowTexts,
} from './tableLayout';

/**
 * Largeur plancher d'une colonne par défaut (`design.minColumnWidth`) : en
 * dessous, un tableau à quatre ou cinq colonnes devient une bouillie de mots
 * coupés sur un écran étroit. Le conteneur défile plutôt que de comprimer.
 */
const MIN_COLUMN_WIDTH_PX = 100;

/** Sous cette largeur de bloc, `mobileLayout: 'cards'` montre le tableau en cartes. */
const MOBILE_CARDS_MAX_WIDTH_PX = 600;

/** Polices d'un tableau neuf (défauts du bloc, d'hier et d'aujourd'hui) : pas
 *  un choix de l'utilisateur. */
const DEFAULT_TABLE_FONTS = [DEFAULT_SITE_FONT, LEGACY_DEFAULT_FONT];

/**
 * Tableau du site — alimenté par le parser proposal ou inséré à la main.
 *
 * Deux dispositions (`design.layout`) : le `<table>` classique, et une grille
 * de cartes que `design.cardHeader` coupe dans un sens ou dans l'autre — une
 * carte par ligne, ou une carte par colonne (le comparatif). La disposition est
 * un choix explicite : rien ne dégrade un tableau de données tout seul.
 *
 * Le nom métier (`tableName`) n'est jamais affiché : c'est la clé de
 * branchement sur une table du document (cf. `utils/webTableRefresh.js`).
 */
const EditableDataTable = ({
  id,
  tableName = '',
  tableConfig = null,
  shouldSplit = false,
  /** Slice d'un tableau découpé : préfixe des chemins d'édition au canvas
   *  (`splitSlices[1].`), vide pour un tableau d'un seul tenant. */
  contentPathPrefix = '',
  columns = [],
  rows = [],
  design: designProp,
  /** Surcharge parser (souvent fourni à la racine des props Puck) */
  footerData: footerDataProp,
  draftStyleContext = {},
  drawMode = false,
  mergedCells = [],
  columnsDisposition = [],
  puck,
}) => {
  /**
   * Un tableau branché s'édite au canvas comme dans le Proposal : intitulés,
   * texte et style des cellules. Ce qui est **dérivé** de la table est réécrit
   * à chaque relecture — une valeur de cellule retapée à la main ne survit donc
   * pas à un rafraîchissement, là-bas comme ici. Un intitulé, lui, survit :
   * il passe dans `tableConfig.customLabel` (cf. `foldCanvasLabels`), tout
   * comme le fait `setTableColumnCustomLabel` côté document.
   */
  const isEditing = !!puck?.isEditing;
  // Modèle web : la table n'a pas encore de lignes. Comme le Proposal classique
  // en modèle (`Table/index.jsx`, `isAdminTable`), le bloc montre un bandeau à
  // son nom au lieu de cellules qu'on prendrait pour du contenu.
  const showTemplateBanner =
    useSyncExternalStore(subscribeTableCatalog, isTemplateTableCatalog) && !!tableName;
  const { splittableTables } = useSyncExternalStore(subscribeTableCatalog, getTableCatalogSnapshot);
  // Le bandeau dit si la table est découpée : il lui faut la liste des tables
  // découpables, que le registre ne charge sinon qu'à la sélection d'un bloc.
  useEffect(() => {
    if (showTemplateBanner) loadTableCatalog();
  }, [showTemplateBanner]);
  const design = useMemo(
    () => ({
      ...storedTableDesign(designProp),
      ...(footerDataProp !== undefined ? { footerData: footerDataProp } : {}),
    }),
    [designProp, footerDataProp],
  );

  const normalizedColumns = useMemo(() => normalizeTableColumns(columns), [columns]);
  const normalizedRows = useMemo(
    () => normalizeTableRows(rows, normalizedColumns.length),
    [rows, normalizedColumns.length],
  );

  /**
   * Fusions de cellules — même moteur que le Proposal
   * (`src/shared/tables/tableStructure.js`). Le calcul se fait à partir des
   * **clés** de lignes et de colonnes, jamais des indices : une fusion survit
   * donc au réordonnancement, et celle dont la ligne ou la colonne a disparu
   * est ignorée au lieu de casser le tableau.
   *
   * Une ligne sans clé n'appartient à aucune fusion : les clés ne sont posées
   * qu'au moment de fusionner (cf. `TableStructureField`).
   */
  const keyedRows = useMemo(
    () => normalizedRows.map((row, idx) => ({ ...row, key: row.key ?? `row-${idx}` })),
    [normalizedRows],
  );

  /**
   * Fusions décidées par la table elle-même, pour un tableau branché : la
   * ligne-titre qui n'a que sa première colonne remplie prend toute la largeur,
   * et les colonnes que l'API marque `merge` fusionnent leurs valeurs répétées.
   * Le document fait exactement ça (`Table/index.jsx`, `hiddenColumnsKeys`) —
   * ici les deux passent par le même moteur de fusion.
   */
  // Les bornes d'une fusion (`from` / `to`) et les colonnes d'une structure
  // visent le libellé d'origine.
  const originByKey = useMemo(
    () => columnOriginLabels(columns, normalizedColumns),
    [normalizedColumns, columns],
  );
  const originLabel = useCallback((col) => originByKey.get(col.key), [originByKey]);
  // Lignes-titre et totaux : seulement d'un tableau à structure déclarée, comme
  // au document (cf. Table/index.jsx).
  const rowStructure = useMemo(
    () => (tableName ? getTableRowStructure(tableName, tableConfig) : null),
    [tableName, tableConfig],
  );
  const rowsByColumnKey = useMemo(
    () => (rowStructure ? typedRowTexts(keyedRows, normalizedColumns, rowStructure, originByKey) : null),
    [rowStructure, keyedRows, normalizedColumns, originByKey],
  );

  /**
   * Lecture interactive (tri, recherche, filtre, replis — cf.
   * tableInteractions) : sur le site et l'aperçu seulement. Au canvas, une
   * cellule s'édite par son indice de ligne : l'ordre doit rester celui de la
   * donnée.
   */
  const interactive = !isEditing;
  const {
    sortable = false, sortColumns = [], searchable = false, filterColumn = '', collapsibleGroups = false,
    crosshairHover = false,
  } = design;
  const crosshair = crosshairHover && interactive;
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const rowTexts = useMemo(
    () => keyedRows.map((row) => normalizedColumns.map((col, idx) => plainCellText(row.cells?.[idx]?.value))),
    [keyedRows, normalizedColumns],
  );
  // Sorte que la forme déclarée de la table donne à un libellé fixe
  // (« MASSE SALARIALE » ouvre un groupe, « TOTAL HT » reste en place).
  const labelKinds = useMemo(() => new Map(
    (tableName ? getTablePreviewLayout(tableName, tableConfig)?.shape ?? [] : [])
      .filter((row) => row.label)
      .map((row) => [row.label, row.kind]),
  ), [tableName, tableConfig]);
  const rowRoles = useMemo(() => keyedRows.map((row, idx) => rowRole({
    rowKind: rowsByColumnKey?.[idx]?.rowKind ?? row.rowKind ?? null,
    labelKind: labelKinds.get(rowTexts[idx].find(Boolean)) ?? null,
    texts: rowTexts[idx],
  })), [keyedRows, rowsByColumnKey, labelKinds, rowTexts]);

  const [sort, setSort] = useState(null);
  const [query, setQuery] = useState('');
  const [filterValue, setFilterValue] = useState(null);
  const [collapsedRows, setCollapsedRows] = useState(() => new Set());
  const filterColumnIndex = normalizedColumns.findIndex((col) => col.key === filterColumn);
  // Tableau à groupes (postes, zones) : le filtre choisit un groupe, pas une
  // valeur de colonne — celles d'une fiche (« Taux horaire ») se répètent.
  const groupOptions = useMemo(() => groupTitles(rowTexts, rowRoles), [rowTexts, rowRoles]);
  const filtersGroups = filterColumnIndex >= 0 && groupOptions.length >= 2;
  // Une fiche par groupe (mêmes lignes partout) : rien à trier dans un groupe.
  const sortsRows = sortable && interactive && !repeatsGroupStructure(rowTexts, rowRoles);
  // Jamais une colonne d'images, de vidéos ou d'icônes, ni une colonne sans
  // valeur ; parmi les autres, celles choisies (aucune choisie : toutes).
  const sortableColumns = useMemo(() => new Set(normalizedColumns.flatMap((col, idx) => (
    (!sortColumns.length || sortColumns.includes(col.key)) && isSortableColumn(col, idx, rowTexts, rowRoles)
      ? [idx] : []
  ))), [normalizedColumns, sortColumns, rowTexts, rowRoles]);
  const activeSort = sortsRows && sort && sortableColumns.has(sort.key) ? sort : null;

  const displayed = useMemo(() => (interactive
    ? visibleRowIndexes(rowTexts, rowRoles, {
      sort: activeSort && { column: activeSort.key, direction: activeSort.dir },
      query: searchable ? query : '',
      filter: !filtersGroups && filterColumnIndex >= 0 && filterValue
        ? { column: filterColumnIndex, value: filterValue }
        : null,
      group: filtersGroups ? filterValue : null,
      collapsed: collapsibleGroups ? collapsedRows : undefined,
    })
    : keyedRows.map((_, idx) => idx)
  ), [interactive, rowTexts, rowRoles, activeSort, searchable, query, filterColumnIndex, filtersGroups,
    filterValue, collapsibleGroups, collapsedRows, keyedRows]);
  // Fusions et traits se calculent sur les lignes montrées, dans leur ordre :
  // une fusion dont une ligne est masquée ou déplacée tombe d'elle-même.
  const displayedRows = useMemo(() => displayed.map((idx) => keyedRows[idx]), [displayed, keyedRows]);
  const displayedRowsByColumnKey = useMemo(
    () => rowsByColumnKey && displayed.map((idx) => rowsByColumnKey[idx]),
    [rowsByColumnKey, displayed],
  );

  const toggleGroup = (rowIdx) => setCollapsedRows((current) => {
    const next = new Set(current);
    if (next.has(rowIdx)) next.delete(rowIdx);
    else next.add(rowIdx);
    return next;
  });

  const autoMerges = useMemo(() => {
    if (!tableName) return null;
    const valueOf = (row, column) => {
      const index = normalizedColumns.findIndex((col) => col.key === column.key);
      return plainCellText(row.cells?.[index]?.value);
    };
    return [
      ...(displayedRowsByColumnKey ? computeLabelRowMerges(displayedRowsByColumnKey, normalizedColumns, undefined, {
        ...tableConfig?.merge,
        columnLabel: originLabel,
      }) : []),
      ...computeRepeatedValueMerges(displayedRows, normalizedColumns, valueOf),
    ];
  }, [tableName, displayedRowsByColumnKey, displayedRows, normalizedColumns, originLabel, tableConfig?.merge]);

  /**
   * Style des lignes (`tableConfig.rowStyles`, réglé dans l'écran de
   * configuration ou venu du document) : mêmes règles et même rendu que le
   * document (cf. tableRowStyle).
   */
  const rowStylesByKey = useMemo(
    () => (rowsByColumnKey ? computeRowStyles(rowsByColumnKey, normalizedColumns, undefined, {
      rowStyles: fromPuckTableConfig(tableConfig)?.rowStyles,
      structure: rowStructure,
    }) : null),
    [rowsByColumnKey, normalizedColumns, tableConfig?.rowStyles, rowStructure],
  );
  const rowRuleAt = (rowIdx) => rowStylesByKey?.get(keyedRows[rowIdx]?.key);
  // Le trait sous une ligne est le `border-bottom` de son `tr` (Mantine) : il
  // prend la couleur de bordure de la ligne, ou à défaut de la suivante —
  // la suivante À L'ÉCRAN (`position` : rang parmi les lignes montrées).
  const rowCss = (position) => rowStyleCss(rowRuleAt(displayed[position]), rowRuleAt(displayed[position + 1]));

  const effectiveMergedCells = useMemo(
    () => (autoMerges?.length ? [...(mergedCells || []), ...autoMerges] : mergedCells),
    [mergedCells, autoMerges],
  );

  const { spanByCell, coveredCells } = useMemo(
    () => computeMergedCellLayout(displayedRows, normalizedColumns, effectiveMergedCells),
    [displayedRows, normalizedColumns, effectiveMergedCells],
  );

  /**
   * Sélection de cellules au Maj+clic, comme dans le Proposal : le premier
   * Maj+clic ancre, le suivant étend le rectangle, et on n'entre jamais en
   * édition. Sans Maj, rien ne change — le clic ouvre la cellule.
   */
  // La sélection de structure n'a de sens que sur un tableau libre : celles
  // d'un tableau branché sont décidées par la table.
  const structurable = isEditing && !tableName;
  const selection = useSyncExternalStore(subscribeTableSelection, getTableSelection);
  const mySelection = selection.blockId === id ? selection : null;
  const anchorRef = useRef(null);

  /**
   * Cases réveillées : au canvas, une case n'a son éditeur TipTap qu'une fois
   * cliquée. Un éditeur par case, c'était 1 400 éditeurs ProseMirror montés
   * d'un coup sur un BPU par postes — plusieurs secondes de gel au passage
   * de « par profils » à « par postes ». Une case réveillée le reste : la
   * démonter au blur perdrait la frappe encore en attente d'envoi.
   */
  const [awakeCells, setAwakeCells] = useState(() => new Set());
  const focusRequestsRef = useRef(new Map());
  const wakeCell = useCallback((contentPath, focusAt) => {
    if (focusRequestsRef.current.has(contentPath)) return;
    focusRequestsRef.current.set(contentPath, focusAt);
    setAwakeCells((current) => new Set(current).add(contentPath));
  }, []);

  const selectCell = useCallback((rowIndex, columnIndex) => {
    const anchor = anchorRef.current;
    if (anchor) {
      setTableCellSelection(id, {
        r0: anchor.rowIndex, r1: rowIndex, c0: anchor.columnIndex, c1: columnIndex,
      });
      return;
    }
    anchorRef.current = { rowIndex, columnIndex };
    setTableCellSelection(id, {
      r0: rowIndex, r1: rowIndex, c0: columnIndex, c1: columnIndex,
    });
  }, [id]);

  const selectColumn = useCallback((columnKey) => {
    const current = mySelection?.columnKeys || [];
    const next = current.includes(columnKey)
      ? current.filter((key) => key !== columnKey)
      : [...current, columnKey];
    setTableColumnSelection(id, next);
  }, [id, mySelection]);

  useEffect(() => {
    if (!mySelection?.rect) anchorRef.current = null;
  }, [mySelection]);

  const cellSelected = useCallback((rowIndex, columnIndex) => {
    const rect = mySelection?.rect;
    if (!rect) return false;
    return rowIndex >= Math.min(rect.r0, rect.r1)
      && rowIndex <= Math.max(rect.r0, rect.r1)
      && columnIndex >= Math.min(rect.c0, rect.c1)
      && columnIndex <= Math.max(rect.c0, rect.c1);
  }, [mySelection]);

  const columnByKey = useMemo(
    () => new Map(normalizedColumns.map((col) => [col.key, col])),
    [normalizedColumns],
  );
  const columnIndexByKey = useMemo(
    () => new Map(normalizedColumns.map((col, idx) => [col.key, idx])),
    [normalizedColumns],
  );

  /** En-tête à deux niveaux : les colonnes rangées sous un groupe. */
  const headerGroups = useMemo(() => {
    if (!Array.isArray(columnsDisposition) || !columnsDisposition.some((col) => col.children)) {
      return null;
    }
    const byKey = new Map(normalizedColumns.map((col) => [col.key, col]));
    const known = (entry) => byKey.has(entry.key);
    const groups = columnsDisposition
      .map((entry) => (entry.children
        ? { ...entry, children: entry.children.filter(known) }
        : entry))
      // Une disposition qui ne décrit plus les colonnes courantes est ignorée,
      // comme côté document : mieux vaut un en-tête plat qu'un en-tête faux.
      .filter((entry) => (entry.children ? entry.children.length > 0 : known(entry)));
    const covered = groups.reduce(
      (sum, entry) => sum + (entry.children ? entry.children.length : 1),
      0,
    );
    if (covered !== normalizedColumns.length) return null;
    return groups;
  }, [columnsDisposition, normalizedColumns]);

  const {
    showHeader,
    headerBgColor,
    headerTextColor,
    headerUppercase,
    borderColor,
    borderWidth,
    borderStyle,
    cornerRadius,
    useCustomBorder,
    isStripped,
    rowEvenBgColor,
    strippedColor,
    highlightOnHover,
    highlightOnHoverColor,
    firstColumnEmphasis,
    size,
  } = withTableStyleDefaults(design);
  const projectConfig = useProposalProjectConfig();

  const {
    textColorEven = '',
    textColorOdd = '',
    fontFamily = DEFAULT_SITE_FONT,
    headerFontFamily,
    headerFontSize,
    cellFontSize,
    cellFontFamily,
    footer,
    footerData,
    stickyHeader = false,
    stickyHeaderOffset = 0,
    maxHeight = 0,
    stickyColumns = 0,
    mobileLayout = 'scroll',
    minColumnWidth = MIN_COLUMN_WIDTH_PX,
  } = design;
  // Hauteur bornée : le tableau défile sur lui-même, dans les deux sens. Son
  // en-tête fixe colle alors en haut de la zone, pas de la page — c'est ce qui
  // le rend compatible avec le défilement latéral.
  const boundedHeight = Math.max(Number(maxHeight) || 0, 0);
  const stickyHeaderOnPage = stickyHeader && !boundedHeight;

  // Ces quatre-là rappellent chacun `withTableStyleDefaults` : cinq recopies de
  // l'objet de style à chaque rendu, pour un résultat qui ne dépend que de
  // `design` et des colonnes. (On ne mémoïse **que du calcul** : figer ce que
  // reçoit un éditeur de cellule a déjà cassé la barre de mise en forme.)
  const { borderScope, spacing, layout, cardHeader } = useMemo(() => ({
    borderScope: resolveBorderScope(design),
    spacing: resolveTableSpacing(design),
    layout: resolveTableLayout(design, normalizedColumns),
    cardHeader: resolveCardHeader(design, normalizedColumns),
  }), [design, normalizedColumns]);

  /**
   * L'œil d'une colonne dans la sidebar l'amène sous les yeux : on fait défiler
   * le conteneur — jamais la page — et on la surligne le temps de la repérer.
   *
   * Le défilement se calcule en coordonnées écran : `offsetLeft` dépendrait de
   * l'ancêtre positionné, qui n'est pas le conteneur.
   */
  const scrollRef = useRef(null);
  const [previewedColumn, setPreviewedColumn] = useState(null);
  const previewTimerRef = useRef(null);

  const previewColumn = useCallback((columnIndex) => {
    setPreviewedColumn(columnIndex);
    clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => setPreviewedColumn(null), 1400);

    const container = scrollRef.current;
    const cell = container?.querySelector(`[data-col-index="${columnIndex}"]`);
    if (!container || !cell) return;
    const cellBox = cell.getBoundingClientRect();
    const viewBox = container.getBoundingClientRect();
    const centered = (cellBox.left - viewBox.left)
      - (container.clientWidth - cellBox.width) / 2;
    container.scrollBy({ left: centered, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!isEditing) return undefined;
    return subscribeColumnPreview((blockId, columnIndex) => {
      if (blockId === id) previewColumn(columnIndex);
    });
  }, [isEditing, id, previewColumn]);

  useEffect(() => () => clearTimeout(previewTimerRef.current), []);

  /**
   * Petit écran : un tableau large y défile mal. En `mobileLayout: 'cards'`,
   * il passe en cartes (une par ligne) sous MOBILE_CARDS_MAX_WIDTH_PX — la
   * largeur du bloc, qui suit aussi les viewports du canvas.
   */
  const wrapperRef = useRef(null);
  const [narrow, setNarrow] = useState(false);
  const cardsWhenNarrow = layout === 'table' && mobileLayout === 'cards';
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!cardsWhenNarrow || !wrapper) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setNarrow(entry.contentRect.width < MOBILE_CARDS_MAX_WIDTH_PX);
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [cardsWhenNarrow, showTemplateBanner, normalizedColumns.length]);
  const shownLayout = cardsWhenNarrow && narrow ? 'cards' : layout;

  /**
   * Colonnes figées : les N premières restent à gauche au défilement latéral
   * (libellés d'une grille tarifaire). Jamais toutes : il faut une colonne qui
   * défile. Leur décalage se mesure sur une case non fusionnée de chacune —
   * les largeurs en % dépendent de la largeur réelle du tableau. Repassé en
   * tableau depuis les cartes, c'est un nouveau `<table>` : on le remesure.
   */
  const stickyCount = shownLayout === 'table'
    ? Math.min(Math.max(Number(stickyColumns) || 0, 0), normalizedColumns.length - 1)
    : 0;
  const [stickyLefts, setStickyLefts] = useState([]);
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!stickyCount || !container) return undefined;
    const measure = () => {
      const lefts = [];
      let offset = 0;
      for (let index = 0; index < stickyCount; index += 1) {
        lefts.push(offset);
        const cell = Array.from(container.querySelectorAll(`[data-col-index="${index}"]`))
          .find((candidate) => candidate.colSpan === 1);
        offset += cell?.getBoundingClientRect().width || 0;
      }
      setStickyLefts((previous) => (
        previous.length === lefts.length && previous.every((left, i) => left === lefts[i])
          ? previous
          : lefts
      ));
    };
    measure();
    const observer = new ResizeObserver(measure);
    const table = container.querySelector('table');
    if (table) observer.observe(table);
    return () => observer.disconnect();
  }, [stickyCount, normalizedColumns, showTemplateBanner]);

  /**
   * Case figée : elle couvre le défilement, donc il lui faut un fond opaque —
   * celui de sa ligne, sinon celui de la page. Une case fusionnée qui déborde
   * la zone figée (ligne-titre pleine largeur) défile, mais son texte reste à
   * gauche (`stickyContent`).
   */
  const stickyCell = (colIdx, colSpan = 1, background) => {
    if (colIdx >= stickyCount) return null;
    if (colIdx + colSpan > stickyCount) return { overflows: true };
    return {
      className: `${styles.stickyCell} ${colIdx + colSpan === stickyCount ? styles.stickyEdge : ''}`.trim(),
      style: {
        '--sticky-left': `${stickyLefts[colIdx] || 0}px`,
        '--sticky-bg': background && background !== 'transparent' ? background : undefined,
      },
    };
  };

  /**
   * Plancher par colonne : les pourcentages se résolvent en px sur la largeur
   * visible, et une colonne qui tomberait sous le plancher l'atteint — le
   * tableau s'élargit et défile (cf. resolveColumnPixelWidths). Sans largeur
   * mesurée encore, les pourcentages et un plancher global.
   */
  const minColumnPx = Math.max(Number(minColumnWidth) || 0, 0);
  const [visibleWidth, setVisibleWidth] = useState(0);
  const scrollsSideways = shownLayout === 'table' && !stickyHeaderOnPage;
  useEffect(() => {
    const container = scrollRef.current;
    if (!scrollsSideways || !container) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setVisibleWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [scrollsSideways, showTemplateBanner, normalizedColumns.length]);
  // Tout tient : on garde les pourcentages. Des px qui font la largeur
  // visible, plus la bordure du tableau, déborderaient d'un pixel.
  const columnPixelWidths = useMemo(() => {
    if (!scrollsSideways) return null;
    const widths = resolveColumnPixelWidths(normalizedColumns, visibleWidth, { minWidth: minColumnPx, stickyCount });
    const total = widths?.reduce((sum, width) => sum + width, 0) ?? 0;
    return total > visibleWidth + 1 ? widths : null;
  }, [scrollsSideways, normalizedColumns, visibleWidth, minColumnPx, stickyCount]);

  const withStickyContent = (sticky, content) => (sticky?.overflows
    ? <div className={styles.stickyContent}>{content}</div>
    : content);

  const headerFontSizePx = headerFontSize || 12;
  const bodyFontSizePx = cellFontSize || 14;
  // Police du document ou du bloc neuf : le thème du site l'emporte. Celle
  // d'un bloc neuf n'est pas un choix : rien d'inline, le tableau suit la
  // police du site (même sans projectConfig, dans l'éditeur de template).
  const familyCss = (family) => {
    const themed = themedTextFontFamily(family, projectConfig, DEFAULT_TABLE_FONTS);
    return themed && !DEFAULT_TABLE_FONTS.includes(themed) ? `${themed}, sans-serif` : undefined;
  };
  const headerFamily = familyCss(headerFontFamily || fontFamily);
  const bodyFamily = familyCss(cellFontFamily || fontFamily);

  const headerStyle = {
    color: headerTextColor,
    backgroundColor: headerBgColor,
    fontSize: `${headerFontSizePx}px`,
    fontFamily: headerFamily,
    ...(headerUppercase ? { textTransform: 'uppercase', letterSpacing: '0.06em' } : {}),
  };

  const bodyTextStyle = {
    fontSize: `${bodyFontSizePx}px`,
    fontFamily: bodyFamily,
  };

  /**
   * Texte éditable au canvas.
   *
   * Trois cas, et un seul est nouveau : en édition, un contenu Draft hérité est
   * converti en HTML à la volée (`draftRawToHtml`) pour que Tiptap le reçoive —
   * la conversion n'est persistée que par la première frappe. En lecture, un
   * contenu Draft jamais touché passe toujours par `renderDraftContent`.
   */
  const readOnlyText = (value, style) => {
    if (isDraftRawContent(value)) {
      return renderDraftContent(value, {
        styleContext: draftStyleContext,
        defaultStyle: { ...style, lineHeight: 'normal' },
      });
    }
    return <PuckInlineRichText html={value || ''} style={style} />;
  };

  const editableText = (value, contentPath, { style, placeholder, editable = true, dormant = false } = {}) => {
    if (isEditing && editable && dormant && !awakeCells.has(contentPath)) {
      return (
        <div
          className={styles.dormantCell}
          tabIndex={0}
          // Puck coupe les clics dans un bloc (`pointer-events: none`), sauf
          // sur les zones éditables qu'il laisse passer — comme l'éditeur.
          data-puck-overlay-portal="true"
          onMouseDown={(event) => {
            // Le focus revient à l'éditeur qui va naître, au point du clic.
            event.preventDefault();
            wakeCell(contentPath, { x: event.clientX, y: event.clientY });
          }}
          onFocus={() => wakeCell(contentPath, 'end')}
        >
          {readOnlyText(value, style)}
        </div>
      );
    }
    if (isEditing && editable) {
      return (
        <PuckInlineRichText
          id={id}
          puck={puck}
          html={draftRawToHtml(value, draftStyleContext)}
          contentPath={contentPath}
          style={style}
          minHeight={20}
          placeholder={placeholder}
          showPlaceholderChip={false}
          reducedToolbar
          // Les cellules vivent dans un conteneur qui masque son débordement
          // horizontal : un menu positionné dans le flux se faisait couper net
          // sur les dernières colonnes. Positionné par rapport au viewport, il
          // s'affranchit du rognage (même raison que la barre d'onglets).
          bubbleStrategy="fixed"
          focusAt={focusRequestsRef.current.get(contentPath)}
          onFocusApplied={() => focusRequestsRef.current.delete(contentPath)}
        />
      );
    }
    return readOnlyText(value, style);
  };

  /**
   * Libellé de colonne — il peut porter du Draft hérité comme du HTML.
   *
   * `editable: false` là où le libellé est **répété** (une fois par ligne dans
   * les dispositions en blocs et en cartes) : deux éditeurs branchés sur le
   * même chemin se désynchronisent. Il s'édite là où il n'apparaît qu'une
   * fois — l'en-tête, ou la première carte.
   */
  const columnLabel = (column, columnIndex, { editable = true, color = headerTextColor } = {}) => editableText(
    column.label,
    `${contentPathPrefix}${puckArrayFieldPath('columns', columnIndex, 'label')}`,
    {
      style: {
        fontFamily: headerFamily,
        fontSize: headerFontSizePx,
        color,
      },
      placeholder: 'Colonne',
      editable,
    },
  );

  /** Libellé de colonne débarrassé de son HTML — pour un texte de substitution. */
  const plainLabel = (column) => String(column?.label || '')
    .replace(/<[^>]+>/g, '')
    .trim();

  /* -------------------------------------------------- lecture interactive */

  const sortDirOf = (columnIndex) => (activeSort?.key === columnIndex ? activeSort.dir : null);
  const columnAriaSort = (columnIndex) => (sortableColumns.has(columnIndex) && sortsRows
    ? ariaSort(sortDirOf(columnIndex))
    : undefined);

  /** En-tête cliquable d'un tableau triable : ascendant, descendant, aucun. */
  const headerLabel = (column, columnIndex) => {
    if (!sortsRows || !sortableColumns.has(columnIndex)) return columnLabel(column, columnIndex);
    return (
      <button
        type="button"
        className={styles.sortButton}
        onClick={() => setSort((current) => nextSort(current, columnIndex))}
        aria-label={`Trier par ${plainLabel(column)}`}
      >
        {columnLabel(column, columnIndex)}
        <SortIcon dir={sortDirOf(columnIndex)} className={styles.sortIcon} />
      </button>
    );
  };

  const groupToggle = (rowIdx, content) => {
    const collapsed = collapsedRows.has(rowIdx);
    const Chevron = collapsed ? TablerIcons.IconChevronRight : TablerIcons.IconChevronDown;
    return (
      // Le libellé replie aussi : le chevron seul est une cible trop petite.
      // Le clic du bouton (clavier compris) remonte jusqu'ici.
      <span className={styles.groupToggleCell} onClick={() => toggleGroup(rowIdx)}>
        <button
          type="button"
          className={styles.groupToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Déplier le groupe' : 'Replier le groupe'}
        >
          <Chevron size={16} stroke={1.8} aria-hidden />
        </button>
        {content}
      </span>
    );
  };

  const filtering = interactive && ((searchable && query.trim()) || (filterColumnIndex >= 0 && filterValue));
  const noResult = filtering && !displayed.some((idx) => rowRoles[idx] === 'item');

  const filterOptions = useMemo(() => {
    if (filtersGroups) return groupOptions;
    return filterColumnIndex >= 0 ? columnFilterValues(rowTexts, rowRoles, filterColumnIndex) : [];
  }, [filtersGroups, groupOptions, rowTexts, rowRoles, filterColumnIndex]);

  /** Recherche et filtre au-dessus du tableau ; inertes au canvas, mais vus. */
  const toolbar = (searchable || filterColumnIndex >= 0) && (
    <div className={styles.toolbar}>
      {searchable && (
        <TextInput
          size="sm"
          className={styles.toolbarSearch}
          placeholder="Rechercher dans le tableau"
          aria-label="Rechercher dans le tableau"
          leftSection={<TablerIcons.IconSearch size={16} stroke={1.8} aria-hidden />}
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          disabled={!interactive}
        />
      )}
      {filterColumnIndex >= 0 && (
        <Select
          size="sm"
          className={styles.toolbarFilter}
          placeholder={plainLabel(normalizedColumns[filterColumnIndex]) || 'Filtrer'}
          aria-label={`Filtrer par ${plainLabel(normalizedColumns[filterColumnIndex])}`}
          data={filterOptions}
          value={filterValue}
          onChange={setFilterValue}
          clearable
          searchable
          disabled={!interactive}
          comboboxProps={{ withinPortal: true }}
        />
      )}
    </div>
  );

  const cellContent = (cell = {}, column, position, { editable = true, placeholder = '', font = null } = {}) => {
    const type = resolveCellType(cell, column);
    // Police et taille réglées sur la ligne ou la case (tableConfig.rowStyles) :
    // le texte porte les siennes, celles de la case ne lui parviennent pas.
    const textStyle = {
      ...bodyTextStyle,
      ...(font?.fontFamily && { fontFamily: `${font.fontFamily}, sans-serif` }),
      ...(font?.fontSize && { fontSize: `${font.fontSize}px` }),
    };

    if (type === 'icon') {
      const Icon = TablerIcons[cell.icon] || TablerIcons.IconCheck;
      return (
        <Icon
          size={Math.max(bodyFontSizePx + 4, 18)}
          stroke={1.6}
          color={cell.badgeColor || undefined}
          aria-hidden
        />
      );
    }

    // Image, vidéo, et l'ancien `media` : tous portent leur source dans
    // `cell.media`, et c'est le média lui-même qui dit comment le cadrer.
    // Image du CRM encore vide : l'image d'attente, comme partout dans l'éditeur.
    const cellMedia = withVariablePlaceholder(cell.media, { isEditing });
    if (type === 'media' || type === 'video' || (type === 'image' && isMediaPresent(cellMedia))) {
      if (!isMediaPresent(cellMedia)) return null;
      const media = normalizeMediaSource(cellMedia);
      const Frame = media.kind === MEDIA_KINDS.image ? ImageFrame : VideoFrame;
      return (
        <Frame
          src={getMediaSrc(media)}
          media={media}
          mediaFit="contain"
          className={styles.cellMedia}
        />
      );
    }

    // Image sans média : URL nue dans `value` — la forme que produit le parser,
    // toujours lue tant que personne n'a choisi d'image dans la sidebar.
    if (type === 'image') {
      return cell.value
        ? <img src={cell.value} alt="" loading="lazy" className={styles.cellImage} />
        : null;
    }

    // `position` absent : la cellule n'est pas adressable (aucun chemin de
    // contenu), on la rend en lecture seule plutôt que d'écrire à l'aveugle.
    if (!position) {
      return renderDraftContent(cell.value, {
        styleContext: draftStyleContext,
        defaultStyle: { ...textStyle, lineHeight: 'normal' },
      });
    }

    return editableText(
      cell.value,
      `${contentPathPrefix}${puckNestedArrayFieldPath('rows', position.rowIndex, 'cells', position.columnIndex, 'value')}`,
      { style: textStyle, placeholder, editable, dormant: true },
    );
  };

  /** Fond d'une ligne : une couleur choisie l'emporte sur l'alternance. */
  const rowBackground = (row, rowIdx) => {
    if (row.backgroundColor) return row.backgroundColor;
    // « transparent » n'est PAS une couleur à poser : en style inline, elle
    // l'emporte sur la règle de survol de Mantine, et le surlignage au survol
    // ne se voyait jamais. Ne rien écrire laisse la règle CSS s'appliquer.
    const even = rowEvenBgColor && rowEvenBgColor !== 'transparent'
      ? rowEvenBgColor
      : undefined;
    if (!isStripped) return even;
    return rowIdx % 2 === 0 ? even : undefined;
  };

  const rowTextColor = (rowIdx) => (rowIdx % 2 !== 0
    ? (textColorOdd || textColorEven)
    : textColorEven) || undefined;

  /** Alternance à la main : hors `<table>`, Mantine ne la pose pas. */
  const stripeBackground = (rowIdx) => (isStripped && rowIdx % 2 !== 0
    ? strippedColor
    : undefined);

  const cellBorder = `${Math.max(Number(borderWidth) || 1, 1)}px ${
    borderStyle === 'none' ? 'solid' : borderStyle
  } ${borderColor}`;

  const wrapperStyle = {
    border: useCustomBorder && borderScope !== 'none' ? cellBorder : undefined,
    borderRadius: cornerRadius ? `${cornerRadius}px` : undefined,
    // `overflow: hidden` rogne les angles arrondis — et rogne aussi tout espoir
    // d'en-tête fixe : il devient l'ancêtre de défilement du `sticky`.
    overflow: cornerRadius && !stickyHeaderOnPage ? 'hidden' : undefined,
    width: '100%',
  };

  /**
   * Le pied s'écrit là où il est lu : le parser le pose à la racine des props,
   * où il masque celui de `design`. Éditer l'autre serait invisible.
   */
  const footerContentPath = footerDataProp !== undefined ? 'footerData' : 'design.footerData';

  /* ---------------------------------------------------------------- table */

  const tableView = (
    <div
      className={[
        styles.scrollContainer,
        stickyHeaderOnPage && styles.scrollContainerSticky,
        boundedHeight && styles.scrollContainerBounded,
      ].filter(Boolean).join(' ')}
      style={boundedHeight ? { maxHeight: `${boundedHeight}px`, overflowY: 'auto' } : undefined}
      ref={scrollRef}
    >
      <Table
        className={styles.table}
        striped={isStripped ? 'even' : false}
        stripedColor={isStripped ? strippedColor : undefined}
        highlightOnHover={highlightOnHover}
        highlightOnHoverColor={
          highlightOnHover && highlightOnHoverColor ? highlightOnHoverColor : undefined
        }
        stickyHeader={stickyHeader}
        stickyHeaderOffset={stickyHeaderOnPage ? Number(stickyHeaderOffset) || 0 : undefined}
        horizontalSpacing={spacing.horizontal}
        verticalSpacing={spacing.vertical}
        borderColor={borderScope !== 'none' ? borderColor : undefined}
        withTableBorder={borderScope === 'all'}
        withColumnBorders={borderScope === 'all'}
        withRowBorders={borderScope !== 'none'}
        style={{
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          width: '100%',
          // Sous cette largeur, `tableLayout: fixed` écrase les colonnes au
          // lieu de les faire déborder : c'est le conteneur qui doit défiler.
          // Sauf en-tête fixe : plus rien ne défile latéralement, une largeur
          // plancher ferait déborder la page entière.
          minWidth: (() => {
            if (stickyHeaderOnPage) return undefined;
            if (columnPixelWidths) return `${columnPixelWidths.reduce((sum, width) => sum + width, 0)}px`;
            return `${normalizedColumns.length * minColumnPx}px`;
          })(),
        }}
      >
        <colgroup>
          {normalizedColumns.map((col, colIdx) => {
            const percentWidth = col.widthPercent != null ? `${col.widthPercent}%` : undefined;
            return (
              <col
                key={col.key}
                style={{ width: columnPixelWidths ? `${columnPixelWidths[colIdx]}px` : percentWidth }}
              />
            );
          })}
        </colgroup>
        {showHeader && (
          <Table.Thead style={{ backgroundColor: headerBgColor }}>
            {headerGroups ? (
              <>
                {/* Premier niveau : un groupe couvre ses colonnes (colSpan),
                    une colonne libre traverse les deux lignes (rowSpan). */}
                <Table.Tr>
                  {headerGroups.map((entry) => {
                    const groupSticky = entry.children
                      ? stickyCell(columnIndexByKey.get(entry.children[0].key), entry.children.length, headerBgColor)
                      : stickyCell(columnIndexByKey.get(entry.key), 1, headerBgColor);
                    return entry.children ? (
                      <Table.Th
                        key={entry.key}
                        colSpan={entry.children.length}
                        className={groupSticky?.className}
                        style={{ ...headerStyle, textAlign: 'center', ...groupSticky?.style }}
                      >
                        {entry.title || ''}
                      </Table.Th>
                    ) : (
                      <Table.Th
                        key={entry.key}
                        rowSpan={2}
                        data-col-index={columnIndexByKey.get(entry.key)}
                        aria-sort={columnAriaSort(columnIndexByKey.get(entry.key))}
                        className={groupSticky?.className}
                        style={{
                          ...headerStyle,
                          textAlign: resolveTextAlign(null, columnByKey.get(entry.key)),
                          verticalAlign: resolveVerticalAlign(null, columnByKey.get(entry.key)),
                          ...groupSticky?.style,
                        }}
                      >
                        {headerLabel(
                          columnByKey.get(entry.key),
                          columnIndexByKey.get(entry.key),
                        )}
                      </Table.Th>
                    );
                  })}
                </Table.Tr>
                {/* Dernière ligne d'en-têtes : son trait du bas est le haut de la
                    1re ligne du corps (cf. rowCss). */}
                <Table.Tr style={rowStyleCss(null, rowRuleAt(0))}>
                  {headerGroups.flatMap((entry) => (entry.children || [])).map((child) => {
                    const childSticky = stickyCell(columnIndexByKey.get(child.key), 1, headerBgColor);
                    return (
                      <Table.Th
                        key={child.key}
                        data-col-index={columnIndexByKey.get(child.key)}
                        aria-sort={columnAriaSort(columnIndexByKey.get(child.key))}
                        className={childSticky?.className}
                        style={{
                          ...headerStyle,
                          textAlign: resolveTextAlign(null, columnByKey.get(child.key)),
                          verticalAlign: resolveVerticalAlign(null, columnByKey.get(child.key)),
                          ...childSticky?.style,
                        }}
                      >
                        {headerLabel(
                          columnByKey.get(child.key),
                          columnIndexByKey.get(child.key),
                        )}
                      </Table.Th>
                    );
                  })}
                </Table.Tr>
              </>
            ) : (
              <Table.Tr style={rowStyleCss(null, rowRuleAt(0))}>
                {normalizedColumns.map((col, colIdx) => {
                  const headSticky = stickyCell(colIdx, 1, headerBgColor);
                  return (
                  <Table.Th
                    key={col.key}
                    data-col-index={colIdx}
                    aria-sort={columnAriaSort(colIdx)}
                    data-col-previewed={previewedColumn === colIdx || undefined}
                    onPointerDownCapture={structurable ? (e) => {
                      if (!isMultiSelectClick(e)) return;
                      e.preventDefault();
                      e.stopPropagation();
                      selectColumn(col.key);
                    } : undefined}
                    className={[
                      headSticky?.className,
                      hoveredColumn === colIdx ? styles.columnHover : '',
                      previewedColumn === colIdx ? styles.columnPreviewed : '',
                      mySelection?.columnKeys?.includes(col.key) ? styles.cellSelected : '',
                    ].filter(Boolean).join(' ') || undefined}
                    style={{
                      ...headerStyle,
                      textAlign: resolveTextAlign(null, col),
                      verticalAlign: resolveVerticalAlign(null, col),
                      ...headSticky?.style,
                    }}
                  >
                    {headerLabel(col, colIdx)}
                  </Table.Th>
                  );
                })}
              </Table.Tr>
            )}
          </Table.Thead>
        )}
        <Table.Tbody
          onPointerOver={crosshair ? (event) => {
            const index = event.target.closest?.('[data-col-index]')?.dataset.colIndex;
            setHoveredColumn(index == null ? null : Number(index));
          } : undefined}
          onPointerLeave={crosshair ? () => setHoveredColumn(null) : undefined}
        >
          {/* `rowIdx` : indice de la ligne dans la donnée (chemins d'édition,
              styles) ; `position` : son rang à l'écran (alternance, traits). */}
          {displayed.map((rowIdx, position) => {
            const row = normalizedRows[rowIdx];
            const collapsible = collapsibleGroups && interactive && rowRoles[rowIdx] === 'header';
            return (
            <Table.Tr
              key={rowIdx}
              className={`${styles.row} ${row.highlight ? styles.rowHighlight : ''}`.trim()}
              style={{
                '--row-bg': rowBackground(row, position),
                color: rowTextColor(position),
                ...rowCss(position),
              }}
            >
              {normalizedColumns.map((col, colIdx) => {
                const cellKey = mergedCellSpanKey(row.key ?? `row-${rowIdx}`, col.key);
                // Case avalée par une fusion : elle n'est pas rendue du tout,
                // c'est l'ancre qui l'occupe par son span.
                if (coveredCells.has(cellKey)) return null;
                const span = spanByCell.get(cellKey);
                // Ancre fusionnée sans contenu propre : elle porte la cellule
                // d'une autre colonne de la fusion (libellé ramené à gauche).
                const contentIdx = span?.contentColumnKey
                  ? normalizedColumns.findIndex((c) => c.key === span.contentColumnKey)
                  : -1;
                const srcIdx = contentIdx >= 0 ? contentIdx : colIdx;
                const srcCol = normalizedColumns[srcIdx];
                // Réglages propres à cette colonne de la ligne, par-dessus ceux de la ligne.
                const cellEntry = cellStyleEntry(rowRuleAt(rowIdx), originLabel(srcCol));
                const sticky = stickyCell(
                  colIdx,
                  span?.colSpan || 1,
                  cellEntry?.background || rowRuleAt(rowIdx)?.background
                    || rowBackground(row, position) || stripeBackground(position),
                );
                const content = cellContent(row.cells[srcIdx], srcCol, {
                  rowIndex: rowIdx,
                  columnIndex: srcIdx,
                }, { font: { ...rowRuleAt(rowIdx), ...cellEntry } });
                return (
                  <Table.Td
                    key={col.key}
                    data-col-index={colIdx}
                    rowSpan={span?.rowSpan}
                    colSpan={span?.colSpan}
                    onPointerDownCapture={structurable ? (e) => {
                      if (!isMultiSelectClick(e)) return;
                      // Avant l'éditeur de texte : Maj+clic sélectionne, il
                      // n'ouvre jamais la cellule.
                      e.preventDefault();
                      e.stopPropagation();
                      selectCell(rowIdx, colIdx);
                    } : undefined}
                    data-cell-selected={cellSelected(rowIdx, colIdx) || undefined}
                    className={[
                      sticky?.className,
                      hoveredColumn === colIdx ? styles.columnHover : '',
                      firstColumnEmphasis && colIdx === 0 ? styles.emphasisCell : '',
                      previewedColumn === colIdx ? styles.columnPreviewed : '',
                      cellSelected(rowIdx, colIdx) ? styles.cellSelected : '',
                    ].filter(Boolean).join(' ') || undefined}
                    style={{
                      ...bodyTextStyle,
                      color: rowTextColor(position),
                      // Alignement : celui de la case, puis de la ligne, puis de la colonne.
                      textAlign: resolveTextAlign(
                        row.cells[srcIdx],
                        (cellEntry?.align ?? rowRuleAt(rowIdx)?.align)
                          ? { textAlign: cellEntry?.align ?? rowRuleAt(rowIdx).align }
                          : srcCol,
                      ),
                      verticalAlign: resolveVerticalAlign(row.cells[srcIdx], srcCol),
                      ...rowCss(position),
                      ...rowStyleCss(cellEntry),
                      ...sticky?.style,
                    }}
                  >
                    {/* Le chevron reste collé à son libellé : c'est leur ensemble
                        qui suit la zone figée. */}
                    {withStickyContent(sticky, collapsible && colIdx === 0
                      ? groupToggle(rowIdx, content)
                      : content)}
                  </Table.Td>
                );
              })}
            </Table.Tr>
            );
          })}
          {noResult && (
            <Table.Tr>
              <Table.Td colSpan={normalizedColumns.length} className={styles.noResult} style={bodyTextStyle}>
                Aucune ligne ne correspond à la recherche.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
        {footer && (
          <Table.Tfoot>
            <Table.Tr>
              <Table.Td colSpan={normalizedColumns.length} style={bodyTextStyle}>
                {editableText(footerData, footerContentPath, {
                  style: bodyTextStyle,
                  placeholder: 'Pied de tableau',
                })}
              </Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        )}
      </Table>
    </div>
  );

  /* ---------------------------------------------------- cartes par ligne */

  /**
   * Libellé de colonne repris dans une carte : il y est répété une fois par
   * ligne, donc éditable dans la **première** seulement.
   */
  // Repli mobile : la carte n'a pas le fond de l'en-tête, son texte (souvent
  // blanc) y serait illisible — le libellé prend la couleur de la carte.
  const entryLabel = (entry, rowIdx, { mobile = false } = {}) => showHeader && (
    <span
      className={styles.entryLabel}
      style={headerUppercase ? { textTransform: 'uppercase' } : undefined}
    >
      {columnLabel(entry.column, entry.columnIndex, {
        editable: rowIdx === 0,
        ...(mobile ? { color: 'inherit' } : {}),
      })}
    </span>
  );

  /**
   * `mobile` : repli d'un tableau sur petit écran. Ses lignes n'ont pas été
   * pensées en cartes : la première colonne en fait le titre, et une case de
   * texte vide (ligne-titre, total sans quantité) n'y laisse pas d'étiquette
   * orpheline. La disposition « Cartes » choisie, elle, reste telle quelle.
   */
  const renderRowCards = ({ mobile = false } = {}) => (
    <div className={styles.cardsGrid}>
      {displayed.map((rowIdx, position) => {
        const row = normalizedRows[rowIdx];
        // Une ligne d'espacement du tableau n'a rien à dire en carte.
        if (mobile && rowTexts[rowIdx].every((text) => !text)
          && normalizedColumns.every((col, idx) => resolveCellType(row.cells[idx] || {}, col) === 'text')) {
          return null;
        }
        const { title: rowTitle, entries: allEntries } = toRowEntries(row, normalizedColumns, {
          firstColumnEmphasis: firstColumnEmphasis || mobile,
        });
        // Sur petit écran, une case vide ne laisse ni étiquette ni titre vide.
        const shown = (entry) => !mobile || rowTexts[rowIdx][entry.columnIndex]
          || resolveCellType(entry.cell, entry.column) !== 'text';
        const title = rowTitle && shown(rowTitle) ? rowTitle : null;
        const entries = allEntries.filter(shown);
        return (
          <div
            key={rowIdx}
            className={`${styles.card} ${row.highlight ? styles.rowHighlight : ''}`}
            style={{
              backgroundColor: row.backgroundColor || stripeBackground(position),
              border: borderScope !== 'none' ? cellBorder : undefined,
              borderRadius: cornerRadius ? `${cornerRadius}px` : undefined,
            }}
          >
            {title && (
              <div
                className={styles.cardTitle}
                style={{ fontFamily: headerFamily }}
              >
                {cellContent(
                  title.cell,
                  title.column,
                  { rowIndex: rowIdx, columnIndex: title.columnIndex },
                  { placeholder: plainLabel(title.column) || 'Titre' },
                )}
              </div>
            )}
            {entries.map((entry) => (
              <div key={entry.column.key} className={styles.cardEntry} style={bodyTextStyle}>
                {entryLabel(entry, rowIdx, { mobile })}
                <div>
                  {cellContent(
                    entry.cell,
                    entry.column,
                    { rowIndex: rowIdx, columnIndex: entry.columnIndex },
                    { placeholder: 'Valeur' },
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
  const rowCardsView = renderRowCards();

  /* --------------------------------------------------- cartes par colonne */

  const compareCards = cardHeader === 'columnTitle'
    ? toCompareCards(normalizedColumns, displayed.map((idx) => normalizedRows[idx]), { firstColumnEmphasis })
    : null;

  const columnCardsView = compareCards && (
    <div className={styles.compareGrid}>
      {compareCards.map((card, cardIdx) => (
        <div
          key={card.column.key}
          className={styles.compareCard}
          style={{
            border: borderScope !== 'none' ? cellBorder : undefined,
            borderRadius: cornerRadius ? `${cornerRadius}px` : undefined,
          }}
        >
          {showHeader && (
            <div className={styles.compareHead} style={headerStyle}>
              {columnLabel(card.column, card.columnIndex)}
            </div>
          )}
          {card.entries.map((entry) => (
            <div
              key={entry.rowIndex}
              className={`${styles.compareRow} ${entry.row.highlight ? styles.rowHighlight : ''}`}
              style={{
                ...bodyTextStyle,
                backgroundColor: entry.row.backgroundColor || stripeBackground(entry.rowIndex),
                borderTop: borderScope !== 'none' ? cellBorder : undefined,
              }}
            >
              {/* Libellés de ligne : seulement quand la première colonne y est
                  consacrée. Ils sont les mêmes dans toutes les cartes, donc
                  éditables dans la première seulement. */}
              {entry.labelColumn && (
                <span className={styles.entryLabel}>
                  {cellContent(
                    entry.labelCell,
                    entry.labelColumn,
                    { rowIndex: entry.rowIndex, columnIndex: 0 },
                    {
                      editable: cardIdx === 0,
                      placeholder: plainLabel(entry.labelColumn) || 'Libellé',
                    },
                  )}
                </span>
              )}
              <span className={styles.compareValue}>
                {cellContent(
                  entry.cell,
                  card.column,
                  { rowIndex: entry.rowIndex, columnIndex: card.columnIndex },
                  { placeholder: 'Valeur' },
                )}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  if (!normalizedColumns.length) return null;

  // Les cartes par colonne ne se dessinent qu'à partir de deux colonnes ;
  // `resolveCardHeader` a déjà écarté le cas, la garde reste pour le rendu.
  const cardsView = (cardHeader === 'columnTitle' && columnCardsView) || rowCardsView;

  const views = {
    table: tableView,
    cards: cardsView,
  };

  if (showTemplateBanner) {
    return (
      <Box id={id} className={styles.templateBanner}>
        <div className={styles.templateBannerTitle}>
          {tableTitleWithOptions(tableName, tableConfig, (key) => i18n.t(key))}
        </div>
        <div className={styles.templateBannerBody}>
          {isSplittableTable(splittableTables, tableName) && i18n.t(
            shouldSplit ? 'table-split-modal-split' : 'table-split-modal-non-split',
          )}
        </div>
      </Box>
    );
  }

  // La barre se pose AU-DESSUS du cadre du tableau, pas dans sa bordure.
  return (
    <>
      {toolbar}
      <Box
      id={id}
      ref={wrapperRef}
      className={styles.wrapper}
      data-layout={shownLayout}
      data-card-header={layout === 'cards' ? cardHeader : undefined}
      data-draw-mode={drawMode || undefined}
      data-size={size}
      style={wrapperStyle}
    >
      {shownLayout === layout ? (views[layout] || tableView) : renderRowCards({ mobile: true })}
      </Box>
    </>
  );
};

export default EditableDataTable;
